import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IDriver } from '../interfaces/driver';
import { IInboxJob, ISupplierInboxJob } from '../interfaces/inbox-job';
import { IInboxJobFilter } from '../interfaces/inbox-job-filter';
import { EditService } from './edit.service';
import { AcceptJobDTO } from '../interfaces/accept-job';
import { JobAcceptResult } from '../enums/job-accept-result';
import { AllocateDriverDTO } from '../interfaces/allocate-driver';
import { TaskCollectionTravelMode } from '../enums/task-collection-travel-mode.enum';
import { RejectJobWithReasonDTO } from '../interfaces/job-rejection/reject-job-with-reason.dto.interface';
import { EventService } from './event.service';
import { IPageResult } from '../interfaces/page-result';
import { AssignTruckTask } from '../interfaces/assign-truck-task';
import { JobAnalyticsService } from './product-analytics/job-analytics.service';
import { JobsService } from './job.service';
import { UserService } from './user.service';
import { ImpersonationInfo } from '../interfaces/user';

@Injectable({ providedIn: 'root' })
export class SupplierInboxJobsService extends EditService<IInboxJob, IInboxJobFilter> {
  baseUrl = '/api/inboxjobs';
  url = this.baseUrl + '/getSupplierJobs';
  entityTitle = 'SupplierInboxJobs';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private eventService: EventService,
    private jobAnalyticsService: JobAnalyticsService,
    private jobService: JobsService,
    private userService: UserService,
  ) {
    super(http, handlersFactory);
  }

  getThePage(filter: IInboxJobFilter): Promise<IPageResult<ISupplierInboxJob>> {
    let params = this.constructHttpParams(filter);

    return this.handleRequest(
      this.http.get<IPageResult<ISupplierInboxJob>>(this.url, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public watchJob(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.baseUrl}/watchJob?id=${id}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job watched.',
    ).then(() => {
      this.eventService.JobWatchedEvent.emit({ id, watched: true });
      this.sendAnalyticsWatchEvent(id);
    });
  }

  async sendAnalyticsWatchEvent(jobId: number): Promise<void> {
    try {
      const resultData = await this.userService.isUserImpersonating();
      const job = await this.jobService.get(jobId);

      if (!job?.orders?.length) return;

      const allTasks: any[] = [];
      for (const order of job.orders) {
        if (order.tasks && Array.isArray(order.tasks)) {
          allTasks.push(...order.tasks);
        }
      }

      this.jobAnalyticsService.sendWatchJobEvent(job.id);
    } catch (error) {
      console.error('Failed to send analytics watch event:', error);
    }
  }

  public unWatchJob(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.baseUrl}/unWatchJob?id=${id}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job Unwatched.',
    ).then(() => {
      this.eventService.JobWatchedEvent.emit({ id, watched: false });
    });
  }

  public acceptJob(dto: AcceptJobDTO): Promise<JobAcceptResult> {
    return this.handleRequest(
      this.http.post<JobAcceptResult>(`${this.baseUrl}/acceptJob`, dto),
      this.handlersFactory.getForReadAll(),
    );
  }

  public rejectJobWithReason(dto: RejectJobWithReasonDTO): Promise<any> {
    let replacer = (key, value) => (value === null ? undefined : value);

    const queryObject = JSON.parse(JSON.stringify(dto, replacer));
    let params = new HttpParams({ fromObject: queryObject });

    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/reject-job-with-reason`, { params }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job hidden.',
    ).then(() => {
      this.eventService.JobRejectionEvent.emit({ action: 'jobRejected', jobId: dto.jobId });
    });
  }

  public unRejectJob(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/unreject-job/${jobId}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job unhidden.',
    ).then(() => {
      this.eventService.JobRejectionEvent.emit({ action: 'jobUnrejected', jobId: jobId });
    });
  }

  public rejectJob(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/reject-job/${jobId}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job hidden.',
    );
  }

  public supplierHandBack(id: number): Promise<boolean> {
    return this.handleRequest(
      this.http.post<boolean>(`${this.baseUrl}/supplierHandBack/${id}`, undefined),
      this.handlersFactory.getForReadAll(),
      'Job has been handed back.',
    );
  }

  public getSupplierUserDepots(): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getSupplierUserDepots`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public allocateDepot(jobId: number, depotId: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/allocateDepot?jobId=${jobId}&depotId=${depotId}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Depot allocated to job.',
    );
  }

  public getNoDriversJobTasks(jobId: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getNoDriversJobTasks?jobId=${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getSupplierUserDrivers(): Promise<IDriver[]> {
    return this.handleRequest(
      this.http.get<IDriver[]>(`${this.baseUrl}/getSupplierUserDrivers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getSupplierUserDriversForJob(jobId: number): Promise<IDriver[]> {
    return this.handleRequest(
      this.http.get<IDriver[]>(`${this.baseUrl}/getSupplierUserDriversForJob?jobId=${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public allocateDriver(allocateDriverDTO: AllocateDriverDTO): Promise<any> {
    return this.handleRequest(
      this.http.post<any>(`${this.baseUrl}/allocateDriver`, allocateDriverDTO),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Driver allocated to job.',
    );
  }

  public getNoTrucksJobTasks(jobId: number): Promise<AssignTruckTask[]> {
    return this.handleRequest(
      this.http.get<AssignTruckTask[]>(`${this.baseUrl}/getNoTrucksJobTasks?jobId=${jobId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getSupplierUserTrucks(): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getSupplierUserTrucks`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public allocateTruck(taskId: number, truckId: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/allocateTruck?taskId=${taskId}&truckId=${truckId}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Truck allocated to job.',
    );
  }

  updateDefaultTaskCollectionTravelMode(travelMode: TaskCollectionTravelMode) {
    return this.handleRequest(
      this.http.patch(`api/organisationSetting/updateDefaultTravelModeSetting/${travelMode}`, {}),
      this.handlersFactory.getForReadAll(),
    );
  }
}
